import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  subscribeToPushPermissionModal,
  closePushPermissionModal,
  markPwaPromptShown,
} from '../../services/pushPermissionModalService';
import { requestPushPermission } from '../../services/pushService';
import { whoAmI } from '../../services/authService';
import { logDebug } from '../../services/loggerService';
import { Modal } from '../ui/Modal';

/**
 * Push-permission value-pitch modal: "Stay in the loop" + an Enable button
 * that calls requestPushPermission() (the button click satisfies the OS
 * user-gesture requirement). Opened from the post-register trigger (push-
 * capable platforms), the iOS-PWA first-launch trigger, and the Profile →
 * Settings "Enable" button.
 *
 * Install directions are NOT here — those live in InstallInstructionsModal,
 * which is what the iOS/Android web registration and Settings flows open.
 * This modal is only ever shown where the Notification API exists.
 *
 * Close behavior: when opened via the iOS-PWA first-launch trigger we
 * persist a one-shot flag on close so we don't spam the user on every
 * launch. Other callers (post-register, Settings) don't set it — they're
 * already one-shot by virtue of their own triggers.
 */
export const PushPermissionModal: React.FC = () => {
  const { t } = useTranslation('auth');
  const [state, setState] = useState<{
    isOpen: boolean;
    fromFirstLaunch: boolean;
    onGranted?: () => void | Promise<void>;
  }>(() => ({
    isOpen: false,
    fromFirstLaunch: false,
  }));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    return subscribeToPushPermissionModal(setState);
  }, []);

  const close = () => {
    if (state.fromFirstLaunch) {
      markPwaPromptShown();
    }
    closePushPermissionModal();
  };

  const handleEnable = async () => {
    setSubmitting(true);
    try {
      // requestPushPermission() does the full chain: ask OS permission
      // (no-op if already granted), get FCM token, PUT to BE. Returns
      // true only when ALL THREE succeed — so this single boolean is
      // the "push delivery is actually wired up" signal we need.
      const granted = await requestPushPermission();
      logDebug('Push permission modal result', undefined, { granted });
      if (granted) {
        if (state.onGranted) {
          // Caller (e.g. Settings push toggle) gets to react AFTER
          // delivery is confirmed possible. Used to flip the BE push
          // pref to true so on-disk pref never diverges from reality.
          // For the Settings path this triggers an explicit PATCH that
          // refreshes activeProfile via the mutation hook.
          try {
            await state.onGranted();
          } catch (err) {
            logDebug('Push permission onGranted callback threw', undefined, {
              error: err instanceof Error ? err.message : String(err),
            });
          }
        }
        // Refresh local profile state to reflect the BE-side auto-flip
        // that fires inside PUT /device when this is the user's first-
        // ever fcmToken (sets notifications.profile.push = true server-
        // side). Without this whoAmI call, the Settings toggle could
        // visually lag behind reality after register-time and iOS-PWA-
        // first-launch grants — push would actually deliver, but the
        // UI would show off until the next natural profile refetch.
        // Failure is non-fatal: the BE state is correct either way.
        try {
          await whoAmI();
        } catch (err) {
          logDebug('Push permission whoami refresh threw', undefined, {
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
    } finally {
      setSubmitting(false);
      // Close regardless of grant/deny outcome. Granted → push wires up
      // via the side effects inside requestPushPermission (token PUT to
      // BE). Denied → terminal; Profile → Settings is the recovery path.
      close();
    }
  };

  return (
    <Modal isOpen={state.isOpen} onClose={close} size="md" zIndexClass="z-50">
      <h2 className="text-lg font-bold text-foreground mb-2">
        {t('push.permTitle')}
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        {t('push.permBody')}
      </p>
      <div className="flex justify-end gap-2">
        <button
          onClick={close}
          data-testid="push-modal-dismiss"
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium text-foreground bg-secondary rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
        >
          {t('push.permLater')}
        </button>
        <button
          onClick={handleEnable}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
        >
          {submitting ? t('push.permEnabling') : t('push.permEnable')}
        </button>
      </div>
    </Modal>
  );
};
